// src/pages/Home.jsx - CLEANED & EXPANDED HERO WITH TOP SEARCH & RIGHT SIDEBAR ITEMS + FLOATING CATEGORIES TAB
import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "react-query";
import { useNavigate, Link } from "react-router-dom";
import { useTenant } from "../context/TenantContext";
import { useCart } from "../context/CartContext";
import ProductGrid from "../components/Product/ProductGrid";
import BookingGrid from "../components/Booking/BookingGrid";
import { sanitizeImageUrl } from "../utils/imageUtils";
import BookingModal from "../components/Booking/BookingModal";
import StorefrontPromoBanner from "../utils/storefrontpromobanner";
import StorefrontContentBlock from "../components/Layout/StorefrontContentBlock";
import StorefrontInlineBanner from "../components/Layout/StorefrontInlineBanner";
import StorefrontHomepageTemplates from "../components/Layout/StorefrontHomepageTemplates";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import SEO from "../components/Common/SEO";
import api from "../services/api";
import { getStorefrontThemeConfig } from "../utils/storefrontTheme";
import { getConfiguredStorefrontBanners } from "../utils/storefrontBanners";
import toast from "react-hot-toast";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, EffectFade } from "swiper/modules";
import {
  FiStar,
  FiUsers,
  FiTruck,
  FiShield,
  FiHeart,
  FiChevronRight,
  FiChevronLeft,
  FiClock,
  FiPackage,
  FiCalendar,
  FiFilter,
  FiSliders,
  FiX,
  FiChevronDown,
  FiRefreshCw,
  FiSearch,
  FiZap,
  FiGift,
  FiArrowRight,
  FiCheckCircle,
  FiPercent,
  FiMenu,
} from "react-icons/fi";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "../styles/home.css";
import "../styles/product.css";
import "../styles/booking.css";

// SVG Placeholder for missing/invalid images
const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300' viewBox='0 0 300 300'%3E%3Crect width='300' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='45%25' font-family='Arial' font-size='48' fill='%23ddd' text-anchor='middle' dy='.3em'%3E📦%3C/text%3E%3Ctext x='50%25' y='55%25' font-family='Arial' font-size='14' fill='%23999' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";

/**
 * Extracts and cleans the primary image URL from a product object.
 */
const getPrimaryImageUrl = (product) => {
  if (!product) return PLACEHOLDER_IMAGE;

  const collectedImages = [];

  const addImage = (value) => {
    if (!value) return;

    if (Array.isArray(value)) {
      value.forEach(addImage);
      return;
    }

    if (typeof value === "object" && value !== null) {
      if (value.url || value.src) {
        collectedImages.push(value.url || value.src);
      }
      return;
    }

    if (typeof value !== "string") return;

    let trimmed = value.trim();
    if (!trimmed) return;

    if (trimmed.startsWith("data:image/")) {
      collectedImages.push(trimmed);
      return;
    }

    if (
      trimmed.match(/^\/9j\//) ||
      trimmed.match(/^iVBORw0KGgo/) ||
      trimmed.match(/^R0lGOD/) ||
      trimmed.match(/^PHN2Zy/) ||
      trimmed.match(/^[A-Za-z0-9+/=]{100,}/)
    ) {
      let mimeType = "image/png";
      if (trimmed.startsWith("/9j/")) {
        mimeType = "image/jpeg";
      } else if (trimmed.startsWith("R0lGOD")) {
        mimeType = "image/gif";
      } else if (trimmed.startsWith("PHN2Zy")) {
        mimeType = "image/svg+xml";
      }

      const base64Clean = trimmed.replace(/\s+/g, "");
      if (
        /^[A-Za-z0-9+/]*={0,2}$/.test(base64Clean) &&
        base64Clean.length % 4 === 0
      ) {
        collectedImages.push(`data:${mimeType};base64,${trimmed}`);
        return;
      }
    }

    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          parsed.forEach(addImage);
          return;
        }
      } catch (error) {
        // Ignore JSON parse errors
      }
    }

    if (trimmed.includes("|")) {
      trimmed.split("|").forEach(addImage);
      return;
    }

    if (trimmed.includes(",") && trimmed.includes("http")) {
      const parts = trimmed.split(",").map((p) => p.trim());
      if (parts.length > 1) {
        parts.forEach(addImage);
        return;
      }
    }

    collectedImages.push(trimmed);
  };

  if (product.images && Array.isArray(product.images)) {
    product.images.forEach(addImage);
  }

  if (collectedImages.length === 0) {
    addImage(product.ImageUrl || product.imageUrl);
    addImage(product.Images || product.images);
    addImage(product.ImageUrls);
    addImage(product.AdditionalImages);
    addImage(product.ImageData);
  }

  const validImages = collectedImages.filter(
    (img, idx, self) =>
      img && typeof img === "string" && self.indexOf(img) === idx,
  );

  if (validImages.length === 0) return PLACEHOLDER_IMAGE;

  const firstImage = validImages[0];

  if (firstImage.startsWith("data:image/")) {
    return firstImage;
  }

  const sanitized = sanitizeImageUrl(firstImage);
  return sanitized || PLACEHOLDER_IMAGE;
};

const INITIAL_PRODUCTS_TO_SHOW = 10;
const PRODUCTS_INCREMENT = 5;
const MAX_VISIBLE_ROOT_CATEGORIES = 9;

const Home = () => {
  const { tenant, settings, loading: tenantLoading } = useTenant();
  const { addToCart, fetchCartFromServer } = useCart();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("products");
  const [addingProductId, setAddingProductId] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Global Top Search query state
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedChildCategory, setSelectedChildCategory] = useState(null);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 10000 });
  const [sortBy, setSortBy] = useState("featured");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Category mega-menu states & Debounce Ref
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [hoveredChildCategory, setHoveredChildCategory] = useState(null);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const hoverTimerRef = useRef(null);

  // Mobile category sheet state
  const [showMobileCategorySheet, setShowMobileCategorySheet] = useState(false);
  const [mobileCategoryDrilldown, setMobileCategoryDrilldown] = useState(null);

  // Show More states
  const [visibleProductsCount, setVisibleProductsCount] = useState(
    INITIAL_PRODUCTS_TO_SHOW,
  );
  const [randomizeKey, setRandomizeKey] = useState(0);
  const [isRandomizing, setIsRandomizing] = useState(false);

  // Hover delay / debounce handlers for smooth mega-menu interaction
  const handleCategoryMouseEnter = (categoryId) => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    hoverTimerRef.current = setTimeout(() => {
      setHoveredCategory(categoryId);
    }, 150);
  };

  const handleCategoryMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    hoverTimerRef.current = setTimeout(() => {
      setHoveredCategory(null);
      setHoveredChildCategory(null);
    }, 200);
  };

  const handlePanelMouseEnter = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
  };

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  // Get homepage display type from settings
  const homepageDisplayType = settings?.homepageDisplayType || "both";
  const storefrontThemeConfig = getStorefrontThemeConfig(
    settings?.storefrontTemplate,
    settings?.storefrontThemeConfig,
  );
  const storefrontContent = storefrontThemeConfig.storefrontContent;
  const mobileLayout = storefrontContent.mobileLayout;

  useEffect(() => {
    setVisibleProductsCount(storefrontContent.productGrid.initialCount);
  }, [storefrontContent.productGrid.initialCount]);

  const showProducts =
    homepageDisplayType === "products" || homepageDisplayType === "both";
  const showBookings =
    homepageDisplayType === "booking" || homepageDisplayType === "both";

  useEffect(() => {
    if (!showProducts && showBookings) {
      setActiveTab("booking");
    } else if (showProducts && !showBookings) {
      setActiveTab("products");
    }
  }, [showProducts, showBookings]);

  useEffect(() => {
    setVisibleProductsCount(INITIAL_PRODUCTS_TO_SHOW);
  }, [
    selectedCategory,
    selectedChildCategory,
    priceRange,
    sortBy,
    globalSearchQuery,
  ]);

  const toggleMobileFilters = () => {
    setIsFilterOpen(!isFilterOpen);
    if (!isFilterOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isFilterOpen) {
        const filterSidebar = document.querySelector(
          ".home-filter-sidebar-mobile",
        );
        const filterToggle = document.querySelector(".mobile-filter-toggle");
        if (
          filterSidebar &&
          !filterSidebar.contains(event.target) &&
          filterToggle &&
          !filterToggle.contains(event.target)
        ) {
          setIsFilterOpen(false);
          document.body.style.overflow = "";
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "";
    };
  }, [isFilterOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isFilterOpen) {
        setIsFilterOpen(false);
        document.body.style.overflow = "";
      }
      if (window.innerWidth > 768 && showMobileCategorySheet) {
        setShowMobileCategorySheet(false);
        setMobileCategoryDrilldown(null);
        document.body.style.overflow = "";
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isFilterOpen, showMobileCategorySheet]);

  const { data: promotionsData } = useQuery(
    ["active-promotions-list", tenant?.TenantID],
    async () => {
      try {
        const response = await api.get("/api/promotions/active");
        return response.data.promotions || [];
      } catch (error) {
        console.error("Error fetching promotions list:", error);
        return [];
      }
    },
    { enabled: !!tenant, staleTime: 300000 },
  );
  const renderStorefrontBlocks = (position) =>
    storefrontContent.contentBlocks
      .filter((block) => block.enabled && block.position === position)
      .map((block) => (
        <StorefrontContentBlock
          key={block.id}
          block={block}
          promotions={promotionsData || []}
          animation={storefrontContent.animation}
        />
      ));

  const productPromotionMap = useMemo(() => {
    const map = new Map();
    if (promotionsData && promotionsData.length > 0) {
      promotionsData.forEach((promo) => {
        const productId = promo.ProductID || promo.productId || promo.ProductId;
        if (productId) {
          map.set(productId, promo);
        }
      });
    }
    return map;
  }, [promotionsData]);

  const getProductPromotion = (productId, categoryId) => {
    if (!productId) return null;
    if (productPromotionMap.has(productId)) {
      return productPromotionMap.get(productId);
    }
    if (categoryId) {
      for (const [id, promo] of productPromotionMap) {
        if (promo.CategoryID === categoryId) {
          return promo;
        }
      }
    }
    return null;
  };

  const { data: productsData, isLoading: productsLoading } = useQuery(
    ["tenant-products", tenant?.TenantID],
    async () => {
      try {
        const [physicalResponse, digitalResponse] = await Promise.all([
          api.get(
            "/api/products?productType=physical&displayType=products&limit=100",
          ),
          api.get(
            "/api/products?productType=digital&displayType=products&limit=100",
          ),
        ]);

        const physicalProducts =
          physicalResponse.data?.products || physicalResponse.data || [];
        const digitalProducts =
          digitalResponse.data?.products || digitalResponse.data || [];

        const rawProducts = [...physicalProducts, ...digitalProducts];

        const normalizedProducts = rawProducts.map((prod) => ({
          ...prod,
          ImageUrl: getPrimaryImageUrl(prod),
        }));

        return {
          products: normalizedProducts,
          pagination:
            physicalResponse.data?.pagination ||
            digitalResponse.data?.pagination ||
            null,
        };
      } catch (error) {
        console.error("Error fetching products:", error);
        return { products: [], pagination: null };
      }
    },
    {
      enabled:
        !!tenant &&
        (showProducts || storefrontContent.homeLayout.showPromotions),
      staleTime: 60000,
      refetchOnWindowFocus: false,
    },
  );

  const promotionCarouselSlides = useMemo(() => {
    const products = Array.isArray(productsData?.products)
      ? productsData.products
      : [];
    const productsById = new Map(
      products
        .filter((product) => product.ProductID)
        .map((product) => [String(product.ProductID).toLowerCase(), product]),
    );
    const slides = [];

    (promotionsData || []).forEach((promotion) => {
      const productIds = [
        promotion.ProductID,
        ...(Array.isArray(promotion.ProductIDs) ? promotion.ProductIDs : []),
      ]
        .filter(Boolean)
        .map((id) => String(id).toLowerCase());
      const uniqueProductIds = [...new Set(productIds)];
      let promotedProducts = uniqueProductIds
        .map((id) => productsById.get(id))
        .filter(Boolean);

      if (promotedProducts.length === 0 && promotion.CategoryID) {
        promotedProducts = products.filter(
          (product) =>
            String(product.CategoryID || "").toLowerCase() ===
            String(promotion.CategoryID).toLowerCase(),
        );
      }

      if (promotedProducts.length > 0) {
        promotedProducts.forEach((product) => {
          slides.push({ promotion, product });
        });
      } else {
        slides.push({ promotion, product: null });
      }
    });

    return slides;
  }, [promotionsData, productsData]);

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery(
    ["public-categories", tenant?.TenantID],
    async () => {
      try {
        const response = await api.get("/api/public/categories/active");
        return response.data?.categories || [];
      } catch (err) {
        console.error("Error fetching categories:", err);
        return [];
      }
    },
    { enabled: !!tenant, staleTime: 300000, retry: 1 },
  );

  const { data: slidesData, isLoading: slidesLoading } = useQuery(
    ["public-slides", tenant?.TenantID],
    async () => {
      try {
        const response = await api.get("/api/public/slides/active");
        const slides = response.data?.slides || [];
        return Array.isArray(slides) ? slides : [];
      } catch (err) {
        console.error("Error fetching slides:", err);
        return [];
      }
    },
    {
      enabled: !!tenant,
      retry: 1,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );

  const { data: bookingData, isLoading: bookingLoading } = useQuery(
    ["tenant-bookings", tenant?.TenantID],
    async () => {
      try {
        const response = await api.get(
          "/api/products?productType=booking&displayType=booking&limit=100",
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching bookings:", error);
        return { products: [] };
      }
    },
    {
      enabled: !!tenant && showBookings,
      staleTime: 60000,
    },
  );

  const { data: recentReviewSummary } = useQuery(
    ["homepage-review-summary", tenant?.TenantID],
    async () => {
      const response = await api.get("/api/reviews/homepage-summary");
      return response.data;
    },
    { enabled: !!tenant && showProducts, staleTime: 60000 },
  );

  const isLoading =
    tenantLoading ||
    productsLoading ||
    categoriesLoading ||
    slidesLoading ||
    (showBookings && bookingLoading);

  const handleAddToCart = async (product, quantity = 1) => {
    if (product.StockQuantity === 0) {
      toast.error("Out of stock", { duration: 2000 });
      return;
    }

    const productId = product.ProductID;
    setAddingProductId(productId);

    try {
      const promotion = getProductPromotion(productId, product.CategoryID);

      let originalPrice = parseFloat(
        product.SellingPrice || product.Price || 0,
      );
      let finalPrice = originalPrice;
      let discountApplied = 0;

      if (promotion) {
        const discountValue = parseFloat(promotion.DiscountValue) || 0;

        if (promotion.DiscountType === "Percentage") {
          finalPrice =
            Math.round(originalPrice * (1 - discountValue / 100) * 100) / 100;
        } else if (promotion.DiscountType === "FixedAmount") {
          finalPrice = Math.max(
            0,
            Math.round((originalPrice - discountValue) * 100) / 100,
          );
        }

        discountApplied = Math.max(0, originalPrice - finalPrice);
      }

      await addToCart(productId, quantity, {
        name: product.Name,
        price: finalPrice,
        originalPrice: originalPrice,
        imageUrl: product.ImageUrl || PLACEHOLDER_IMAGE,
        sku: product.SKU,
        promotion: promotion,
        discountApplied: discountApplied,
      });

      await fetchCartFromServer(true);
    } catch (error) {
      console.error("Add to cart error:", error);
      toast.error("Failed to add to cart");
    } finally {
      setTimeout(() => setAddingProductId(null), 500);
    }
  };

  const handleBookNow = (booking) => {
    setSelectedBooking(booking);
    setShowBookingModal(true);
  };

  const handleBookingModalClose = () => {
    setShowBookingModal(false);
    setSelectedBooking(null);
  };

  const handleBookingSuccess = () => {
    toast.success(
      "Booking request submitted successfully! We will contact you to confirm.",
      {
        duration: 5000,
      },
    );
  };

  const categoryTree = useMemo(() => {
    const all = Array.isArray(categoriesData) ? categoriesData : [];

    const byParent = {};
    all.forEach((cat) => {
      const pid = cat.ParentCategoryID || "root";
      if (!byParent[pid]) byParent[pid] = [];
      byParent[pid].push(cat);
    });

    const build = (parentId) => {
      const list = byParent[parentId] || [];
      return list.map((cat) => ({
        id: cat.CategoryID,
        name: cat.Name || "Unnamed",
        raw: cat,
        children: build(cat.CategoryID),
      }));
    };

    return build("root");
  }, [categoriesData]);

  const flatCategories = useMemo(() => {
    const out = [];
    const walk = (nodes, parents = []) => {
      nodes.forEach((n) => {
        out.push({ ...n, parents });
        if (n.children?.length) walk(n.children, [...parents, n]);
      });
    };
    walk(categoryTree);
    return out;
  }, [categoryTree]);

  const searchResults = useMemo(() => {
    const term = categorySearchTerm.trim().toLowerCase();
    if (!term) return null;
    return flatCategories
      .filter((c) => c.name.toLowerCase().includes(term))
      .slice(0, 12);
  }, [categorySearchTerm, flatCategories]);

  const findCategoryById = (id) =>
    flatCategories.find((c) => c.id === id) || null;

  const filteredProducts = useMemo(() => {
    let products = Array.isArray(productsData?.products)
      ? productsData.products
      : [];

    if (globalSearchQuery.trim()) {
      const query = globalSearchQuery.toLowerCase();
      products = products.filter(
        (p) =>
          (p.Name && p.Name.toLowerCase().includes(query)) ||
          (p.Description && p.Description.toLowerCase().includes(query)) ||
          (p.SKU && p.SKU.toLowerCase().includes(query)),
      );
    }

    if (selectedCategory !== "all") {
      if (selectedChildCategory) {
        products = products.filter(
          (p) => p.CategoryID === selectedChildCategory,
        );
      } else {
        const descendants = new Set();
        const collect = (node) => {
          descendants.add(node.id);
          node.children?.forEach(collect);
        };
        const parentNode = findCategoryById(selectedCategory);
        if (parentNode) collect(parentNode);

        products = products.filter((p) => descendants.has(p.CategoryID));
      }
    }

    products = products.filter((p) => {
      const price = p.SellingPrice || 0;
      return price >= priceRange.min && price <= priceRange.max;
    });

    switch (sortBy) {
      case "price-low":
        products.sort((a, b) => (a.SellingPrice || 0) - (b.SellingPrice || 0));
        break;
      case "price-high":
        products.sort((a, b) => (b.SellingPrice || 0) - (a.SellingPrice || 0));
        break;
      case "newest":
        products.sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt));
        break;
      case "popular":
        products.sort((a, b) => (b.Rating || 0) - (a.Rating || 0));
        break;
      default:
        break;
    }

    const hasActiveFilters =
      selectedCategory !== "all" ||
      selectedChildCategory !== null ||
      priceRange.min > 0 ||
      priceRange.max < 10000 ||
      sortBy !== "featured" ||
      globalSearchQuery.trim() !== "";

    if (!hasActiveFilters) {
      const shuffled = [...products];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      products = shuffled;
    }

    return products;
  }, [
    productsData,
    selectedCategory,
    selectedChildCategory,
    priceRange,
    sortBy,
    globalSearchQuery,
    randomizeKey,
    flatCategories,
  ]);

  const reviewsByProductId = useMemo(() => {
    const map = new Map();
    const recentReviews = recentReviewSummary?.reviews || [];
    const latestReviewByProductId = new Map();
    recentReviews.forEach((review) => {
      const productId = String(review.ProductID || "").toLowerCase();
      if (productId && !latestReviewByProductId.has(productId)) {
        latestReviewByProductId.set(productId, review);
      }
    });

    (productsData?.products || []).forEach((product) => {
      if (!product.ProductID) return;
      const productId = String(product.ProductID).toLowerCase();
      const latestReview = latestReviewByProductId.get(productId);
      map.set(String(product.ProductID).toLowerCase(), {
        reviews: latestReview ? [latestReview] : [],
        averageRating: product.reviewStats?.rating || 0,
        totalReviews: product.reviewStats?.count || 0,
      });
    });
    return map;
  }, [productsData, recentReviewSummary]);

  const productsWithReviews = useMemo(() => {
    return filteredProducts.map((product) => {
      const reviewData = reviewsByProductId.get(
        String(product.ProductID).toLowerCase(),
      );

      const promo = getProductPromotion(product.ProductID, product.CategoryID);
      let originalPrice = parseFloat(product.SellingPrice || 0);
      let sellingPrice = originalPrice;

      if (promo) {
        const discountValue = parseFloat(promo.DiscountValue) || 0;
        if (promo.DiscountType === "Percentage") {
          sellingPrice =
            Math.round(originalPrice * (1 - discountValue / 100) * 100) / 100;
        } else if (promo.DiscountType === "FixedAmount") {
          sellingPrice = Math.max(
            0,
            Math.round((originalPrice - discountValue) * 100) / 100,
          );
        }
      }

      return {
        ...product,
        SellingPrice: sellingPrice,
        OriginalPrice: promo ? originalPrice : product.OriginalPrice || 0,
        reviewStats: reviewData
          ? {
              rating: reviewData.averageRating,
              count: reviewData.totalReviews,
            }
          : product.reviewStats || { rating: 0, count: 0 },
        latestReview:
          reviewData?.reviews?.length > 0 ? reviewData.reviews[0] : null,
        isBestSeller: (reviewData?.totalReviews || 0) >= 5,
      };
    });
  }, [filteredProducts, reviewsByProductId, productPromotionMap]);

  const visibleProducts = useMemo(() => {
    return productsWithReviews.slice(0, visibleProductsCount);
  }, [productsWithReviews, visibleProductsCount]);

  const hasMoreProducts = visibleProductsCount < productsWithReviews.length;

  const handleShowMore = () => {
    setVisibleProductsCount((prev) =>
      Math.min(prev + PRODUCTS_INCREMENT, productsWithReviews.length),
    );
  };

  const handleRandomize = () => {
    setIsRandomizing(true);
    setRandomizeKey((prev) => prev + 1);
    setVisibleProductsCount(storefrontContent.productGrid.initialCount);
    setTimeout(() => setIsRandomizing(false), 500);
  };

  const resetFilters = () => {
    setSelectedCategory("all");
    setSelectedChildCategory(null);
    setPriceRange({ min: 0, max: 10000 });
    setSortBy("featured");
    setGlobalSearchQuery("");
    setVisibleProductsCount(storefrontContent.productGrid.initialCount);
    setCategorySearchTerm("");

    if (window.innerWidth <= 768) {
      setIsFilterOpen(false);
      document.body.style.overflow = "";
    }
  };

  const clearCategoryLevel = () => {
    setSelectedCategory("all");
    setSelectedChildCategory(null);
    setVisibleProductsCount(INITIAL_PRODUCTS_TO_SHOW);
    setHoveredCategory(null);
    setHoveredChildCategory(null);
    if (window.innerWidth <= 768) {
      setIsFilterOpen(false);
      document.body.style.overflow = "";
    }
  };

  const clearChildCategoryLevel = () => {
    setSelectedChildCategory(null);
    setVisibleProductsCount(INITIAL_PRODUCTS_TO_SHOW);
    setHoveredChildCategory(null);
  };

  const handleCategorySelect = (categoryId) => {
    if (categoryId === "all") {
      clearCategoryLevel();
      return;
    }

    if (selectedCategory === categoryId && !selectedChildCategory) {
      clearCategoryLevel();
    } else {
      setSelectedCategory(categoryId);
      setSelectedChildCategory(null);
      setVisibleProductsCount(INITIAL_PRODUCTS_TO_SHOW);
      if (window.innerWidth <= 768) {
        setIsFilterOpen(false);
        document.body.style.overflow = "";
      }
    }
  };

  const handleChildCategorySelect = (childId) => {
    if (selectedChildCategory === childId) {
      clearChildCategoryLevel();
    } else {
      setSelectedChildCategory(childId);
      setVisibleProductsCount(INITIAL_PRODUCTS_TO_SHOW);
    }
  };

  const handleSearchResultClick = (result) => {
    if (result.parents.length > 0) {
      setSelectedCategory(result.parents[0].id);
      setSelectedChildCategory(result.id);
    } else {
      setSelectedCategory(result.id);
      setSelectedChildCategory(null);
    }
    setVisibleProductsCount(INITIAL_PRODUCTS_TO_SHOW);
    setCategorySearchTerm("");
    if (window.innerWidth <= 768) {
      setIsFilterOpen(false);
      document.body.style.overflow = "";
    }
  };

  // Mobile category sheet handlers
  const openMobileCategorySheet = () => {
    setShowMobileCategorySheet(true);
    setMobileCategoryDrilldown(null);
    document.body.style.overflow = "hidden";
  };

  const closeMobileCategorySheet = () => {
    setShowMobileCategorySheet(false);
    setMobileCategoryDrilldown(null);
    document.body.style.overflow = "";
  };

  const handleMobileCategorySelect = (categoryId) => {
    if (categoryId === "all") {
      clearCategoryLevel();
      closeMobileCategorySheet();
      return;
    }

    const catObj = categoryTree.find((c) => c.id === categoryId);
    if (
      catObj &&
      catObj.children.length > 0 &&
      mobileCategoryDrilldown !== categoryId
    ) {
      setMobileCategoryDrilldown(categoryId);
      return;
    }

    setSelectedCategory(categoryId);
    setSelectedChildCategory(null);
    setVisibleProductsCount(INITIAL_PRODUCTS_TO_SHOW);
    closeMobileCategorySheet();
  };

  const handleMobileChildSelect = (childId) => {
    setSelectedChildCategory(childId);
    setVisibleProductsCount(INITIAL_PRODUCTS_TO_SHOW);
    closeMobileCategorySheet();
  };

  const hasActiveFilters =
    selectedCategory !== "all" ||
    selectedChildCategory !== null ||
    priceRange.min > 0 ||
    priceRange.max < 10000 ||
    sortBy !== "featured" ||
    globalSearchQuery.trim() !== "";

  if (isLoading) {
    return <LoadingSpinner fullScreen text="Loading your experience..." />;
  }

  if (!tenant) {
    return (
      <div className="error-container">
        <div className="error-content">
          <h2>No shop configuration found.</h2>
          <p>Please check back later.</p>
        </div>
      </div>
    );
  }

  const getDefaultSlides = () => [
    {
      SlideID: "default-1",
      Title: "NEW COLLECTION",
      Description: "Summer Vibes",
      LinkUrl: "/shop",
      ImageData:
        "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1200",
      ImageAltText: "Summer collection",
      DisplayOrder: 1,
    },
    {
      SlideID: "default-2",
      Title: "Limited Edition",
      Description: "Exclusive Designs",
      LinkUrl: "/shop",
      ImageData:
        "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200",
      ImageAltText: "Limited edition products",
      DisplayOrder: 2,
    },
    {
      SlideID: "default-3",
      Title: "Up to 50% Off",
      Description: "Season Sale",
      LinkUrl: "/shop",
      ImageData:
        "https://images.unsplash.com/photo-1445205170230-053b83016050?w=1200",
      ImageAltText: "Seasonal sale",
      DisplayOrder: 3,
    },
  ];

  let heroSlides = [];
  if (slidesData && slidesData.length > 0) {
    heroSlides = slidesData.map((slide) => {
      const safeImage = sanitizeImageUrl(
        slide.ImageData || slide.image || slide.ImageUrl,
      );
      return {
        id: slide.SlideID || `slide-${Math.random()}`,
        title: slide.Title || "",
        subtitle: slide.Description || "",
        buttonText: "Shop Now",
        image: safeImage || PLACEHOLDER_IMAGE,
        link: slide.LinkUrl || "/shop",
        altText: slide.ImageAltText || slide.Title || "Slide image",
        color: tenant?.PrimaryColor || "#2c7da0",
      };
    });
  } else {
    heroSlides = getDefaultSlides();
  }
  const configuredBanners = getConfiguredStorefrontBanners(
    storefrontContent.heroSlides,
    Array.isArray(slidesData) ? slidesData : [],
    tenant?.PrimaryColor,
  );
  const configuredHeroSlides = configuredBanners
    .filter((slide) => slide.placement === "hero" && slide.pagePath === "/")
    .map((slide) => ({
      ...slide,
      image: slide.image || PLACEHOLDER_IMAGE,
    }));
  const configuredSlideIds = new Set(
    configuredBanners.flatMap((slide) => [
      slide.id.replace(/^theme-slide-/, ""),
      slide.sourceSlideId,
    ]),
  );
  const inlineHeroBanners = configuredBanners.filter(
    (slide) => slide.placement === "inline" && slide.pagePath === "/",
  );
  heroSlides = [
    ...configuredHeroSlides,
    ...heroSlides.filter((slide) => !configuredSlideIds.has(String(slide.id))),
  ];
  const allProducts = Array.isArray(productsData?.products)
    ? productsData.products
    : Array.isArray(productsData)
      ? productsData
      : [];

  const bookings = Array.isArray(bookingData?.products)
    ? bookingData.products
    : Array.isArray(bookingData)
      ? bookingData
      : [];
  const bookingQuery = globalSearchQuery.trim().toLowerCase();
  const visibleBookings = bookingQuery
    ? bookings.filter(
        (booking) =>
          booking.Name?.toLowerCase().includes(bookingQuery) ||
          booking.Description?.toLowerCase().includes(bookingQuery),
      )
    : bookings;

  const allCategories = Array.isArray(categoriesData) ? categoriesData : [];

  const getCategoryIcon = (categoryName) => {
    const name = (categoryName || "").toLowerCase();
    if (name.includes("men") || name.includes("male")) return "👔";
    if (name.includes("women") || name.includes("female")) return "👗";
    if (name.includes("shoe") || name.includes("footwear")) return "👟";
    if (name.includes("accessorie")) return "🕶️";
    if (name.includes("bag")) return "👜";
    if (name.includes("watch")) return "⌚";
    if (name.includes("jewelry") || name.includes("jewellery")) return "💍";
    if (name.includes("electronics") || name.includes("gadget")) return "📱";
    if (name.includes("home") || name.includes("living")) return "🏠";
    if (name.includes("sports") || name.includes("fitness")) return "⚽";
    if (name.includes("beauty") || name.includes("cosmetic")) return "💄";
    if (name.includes("kids") || name.includes("children")) return "🧸";
    if (name.includes("baby")) return "👶";
    if (name.includes("grocery") || name.includes("food")) return "🛒";
    if (name.includes("health") || name.includes("wellness")) return "💊";
    if (name.includes("pet")) return "🐾";
    if (name.includes("automotive") || name.includes("car")) return "🚗";
    if (name.includes("garden") || name.includes("plant")) return "🌱";
    if (name.includes("hardware") || name.includes("tool")) return "🔧";
    if (name.includes("gift")) return "🎁";
    if (name.includes("craft")) return "🎨";
    if (name.includes("musical") || name.includes("instrument")) return "🎸";
    if (name.includes("seasonal") || name.includes("holiday")) return "🎄";
    return "📦";
  };

  const parentCategories = allCategories.filter(
    (cat) => !cat.ParentCategoryID || cat.ParentCategoryID === "",
  );

  const childCategoriesMap = {};
  allCategories.forEach((cat) => {
    if (cat.ParentCategoryID) {
      if (!childCategoriesMap[cat.ParentCategoryID]) {
        childCategoriesMap[cat.ParentCategoryID] = [];
      }
      childCategoriesMap[cat.ParentCategoryID].push(cat);
    }
  });

  const categories = parentCategories.map((cat) => {
    let icon = cat.Icon || null;
    let iconType = cat.IconType || "emoji";
    let imageUrl = cat.ImageUrl || null;

    if (icon && typeof icon === "string") {
      const safeIcon = sanitizeImageUrl(icon);
      if (safeIcon) {
        if (
          safeIcon.startsWith("data:image/") ||
          safeIcon.startsWith("/uploads/") ||
          safeIcon.startsWith("http")
        ) {
          iconType = "image";
          imageUrl = safeIcon;
          icon = safeIcon;
        } else {
          iconType = "emoji";
        }
      }
    }

    if (imageUrl && !icon) {
      icon = sanitizeImageUrl(imageUrl) || imageUrl;
      iconType = "image";
    }

    if (!icon) {
      icon = getCategoryIcon(cat.Name || cat.name || "");
      iconType = "emoji";
    }

    return {
      id: cat.CategoryID || cat.id,
      name: cat.Name || cat.name || "Unnamed",
      icon: icon,
      iconType: iconType,
      imageUrl: imageUrl,
      iconColor: cat.IconColor || null,
      slug: (cat.Name || cat.name || "").toLowerCase(),
      hasChildren: (childCategoriesMap[cat.CategoryID] || []).length > 0,
      children: childCategoriesMap[cat.CategoryID] || [],
    };
  });

  const handleSlideClick = (slide) => {
    if (slide.link && slide.link !== "#" && slide.link !== "") {
      if (slide.link.startsWith("http")) {
        window.open(slide.link, "_blank", "noopener,noreferrer");
      } else {
        navigate(slide.link);
      }
    } else {
      navigate("/shop");
    }
  };

  const allReviews = (recentReviewSummary?.reviews || []).map((review) => ({
    ...review,
    productName: review.ProductName || "Product",
    productId: review.ProductID,
    productImage: getPrimaryImageUrl({ ImageUrl: review.ProductImage }),
  }));

  const displayReviews = allReviews.slice(0, 6);

  const renderStars = (rating) => (
    <div className="testimonial-stars">
      {[1, 2, 3, 4, 5].map((star) => (
        <FiStar
          key={star}
          className={`testimonial-star ${star <= rating ? "filled" : ""}`}
        />
      ))}
    </div>
  );

  const getAvatarColor = (name) => {
    const colors = [
      "#2c7da0",
      "#61a5c2",
      "#89c2d9",
      "#2a9d8f",
      "#e9c46a",
      "#f4a261",
      "#e76f51",
    ];
    const index = (name?.length || 0) % colors.length;
    return colors[index];
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.charAt(0).toUpperCase();
  };

  const trustBadges = [
    {
      icon: <FiHeart />,
      title: "Quality Products",
      description: "Curated with care",
    },
    {
      icon: <FiUsers />,
      title: "Happy Customers",
      description: "Trusted by many",
    },
    {
      icon: <FiShield />,
      title: "Secure Shopping",
      description: "Safe & protected",
    },
    {
      icon: <FiTruck />,
      title: "Fast Delivery",
      description: "Quick & reliable",
    },
  ];

  const averageRating = Number(recentReviewSummary?.averageRating || 0).toFixed(
    1,
  );

  const showTabs = showProducts && showBookings;
  const bookingToggle = storefrontContent.bookingToggle;
  const storefrontPrimaryColor =
    settings?.storefrontThemeConfig?.primaryColor || tenant?.PrimaryColor || "#2c7da0";

  const rootCategoriesToShow = showAllCategories
    ? categoryTree
    : categoryTree.slice(0, MAX_VISIBLE_ROOT_CATEGORIES);
  const categoryCardsToShow = showAllCategories
    ? categories
    : categories.slice(0, MAX_VISIBLE_ROOT_CATEGORIES);

  // Tenant logo / image for the branding card
  const tenantLogo =
    settings?.LogoUrl ||
    settings?.Logo ||
    tenant?.LogoUrl ||
    tenant?.Logo ||
    null;
  const tenantHeroImage =
    settings?.HeroImageUrl || tenant?.HeroImageUrl || null;

  // Look up the currently hovered category object
  const hoveredCategoryObj = hoveredCategory
    ? categoryTree.find((c) => c.id === hoveredCategory) || null
    : null;

  const renderCategoryMegaMenu = () => (
    <>
      <label className="filter-label">Shop by category</label>

      <div className="category-search-box">
        <FiSearch size={14} />
        <input
          type="text"
          placeholder="Filter categories..."
          value={categorySearchTerm}
          onChange={(e) => setCategorySearchTerm(e.target.value)}
        />
        {categorySearchTerm && (
          <button
            type="button"
            className="category-search-clear"
            onClick={() => setCategorySearchTerm("")}
          >
            <FiX size={12} />
          </button>
        )}
      </div>

      {searchResults && (
        <div className="category-search-results">
          {searchResults.length === 0 ? (
            <div className="category-search-empty">No categories found</div>
          ) : (
            searchResults.map((result) => {
              const isActive =
                selectedChildCategory === result.id ||
                selectedCategory === result.id;
              return (
                <button
                  key={result.id}
                  type="button"
                  className={`category-search-item ${isActive ? "active" : ""}`}
                  onClick={() => handleSearchResultClick(result)}
                >
                  {result.parents.length > 0 && (
                    <span className="category-search-parents">
                      {result.parents.map((p) => p.name).join(" › ")} ›
                    </span>
                  )}
                  <span className="category-search-name">{result.name}</span>
                </button>
              );
            })
          )}
        </div>
      )}

      {!searchResults && (
        <>
          <button
            type="button"
            className={`category-list-row all-row ${
              selectedCategory === "all" && !selectedChildCategory
                ? "active"
                : ""
            }`}
            onClick={() => handleCategorySelect("all")}
          >
            <span className="category-list-name">All Categories</span>
          </button>

          <ul className="category-list">
            {rootCategoriesToShow.map((cat) => {
              const catObj = categories.find((c) => c.id === cat.id);
              const icon = catObj?.icon;
              const iconType = catObj?.iconType;

              return (
                <li
                  key={cat.id}
                  className="category-list-item"
                  onMouseEnter={() => handleCategoryMouseEnter(cat.id)}
                >
                  <button
                    type="button"
                    className={`category-list-row ${
                      selectedCategory === cat.id ? "active" : ""
                    } ${hoveredCategory === cat.id ? "hovered" : ""}`}
                    aria-pressed={selectedCategory === cat.id}
                    aria-expanded={
                      cat.children.length > 0 &&
                      hoveredCategory === cat.id
                    }
                    onClick={() => {
                      handleCategorySelect(cat.id);
                      setHoveredCategory(
                        cat.children.length > 0 ? cat.id : null,
                      );
                    }}
                  >
                    <span className="category-list-icon">
                      {iconType === "image" && icon ? (
                        <img
                          src={icon}
                          alt={cat.name}
                          className="category-list-img"
                        />
                      ) : (
                        <span className="category-list-emoji">
                          {icon || "📦"}
                        </span>
                      )}
                    </span>
                    <span className="category-list-name">{cat.name}</span>
                    {cat.children.length > 0 && (
                      <FiChevronRight
                        size={14}
                        className="category-list-arrow"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {categoryTree.length > MAX_VISIBLE_ROOT_CATEGORIES && (
            <button
              type="button"
              className="category-show-all"
              onClick={() => setShowAllCategories((v) => !v)}
            >
              {showAllCategories
                ? "Show less"
                : `More Categories (${categoryTree.length})`}
              <FiChevronDown
                size={14}
                style={{
                  transform: showAllCategories ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s ease",
                }}
              />
            </button>
          )}
        </>
      )}

      {(selectedCategory !== "all" || selectedChildCategory) && (
        <div className="category-active-pill">
          <span>
            {selectedChildCategory
              ? findCategoryById(selectedChildCategory)?.name
              : findCategoryById(selectedCategory)?.name}
          </span>
          <button
            type="button"
            onClick={clearCategoryLevel}
            title="Clear category"
          >
            <FiX size={12} />
          </button>
        </div>
      )}
    </>
  );

  const homepageTemplateIds = [
    "modern-minimal",
    "luxury-boutique",
    "bold-contemporary",
    "fashion-lifestyle",
    "everyday-essentials",
  ];
  if (showProducts && homepageTemplateIds.includes(settings?.storefrontTemplate)) {
    return (
      <StorefrontHomepageTemplates
        template={settings.storefrontTemplate}
        tenant={tenant}
        config={storefrontThemeConfig}
        categories={categories}
        products={productsWithReviews}
        promotions={promotionsData || []}
        configuredBanners={getConfiguredStorefrontBanners(
          storefrontContent.heroSlides.filter(
            (slide) => slide.placement === "inline",
          ),
          Array.isArray(slidesData) ? slidesData : [],
          tenant?.PrimaryColor,
          Array.isArray(storefrontContent.homepage.excludedHeroSlideIds)
            ? storefrontContent.homepage.excludedHeroSlideIds
            : [],
        )}
      />
    );
  }

  return (
    <div
      className="home-page"
      data-home-layout={storefrontContent.homeLayout.preset}
      data-home-hero-size={storefrontContent.homeLayout.heroSize}
      data-home-category-menu-size={storefrontContent.homeLayout.categoryMenuSize}
      data-home-promotion-size={storefrontContent.homeLayout.promotionSize}
      data-home-category-visible={
        activeTab === "products" &&
        storefrontContent.homeLayout.showCategoryMenu
          ? "true"
          : "false"
      }
      data-home-promotions-visible={
        storefrontContent.homeLayout.showPromotions ? "true" : "false"
      }
      data-home-store-tile-visible={
        storefrontContent.homeLayout.showStoreTile ? "true" : "false"
      }
      data-mobile-hero-height={mobileLayout.heroHeight}
      data-mobile-hero-alignment={mobileLayout.heroAlignment}
      data-mobile-category-side={mobileLayout.categoryTabSide}
    >
      <SEO
        title={tenant?.MetaTitle || tenant?.BusinessName}
        description={tenant?.MetaDescription || tenant?.BusinessDescription}
      />
      <StorefrontPromoBanner placement="Hero" />

      {/* MOBILE FLOATING CATEGORIES TAB */}
      {activeTab === "products" &&
        storefrontContent.homeLayout.showCategoryMenu && (
        <button
          className="mobile-category-tab"
          onClick={openMobileCategorySheet}
          aria-label="Browse categories"
        >
          <span className="category-tab-icon">
            <FiMenu size={20} />
          </span>
          <span className="category-tab-text">CATEGORIES</span>
        </button>
      )}

      {/* HOMEPAGE SEARCH, HERO, CATEGORY AND PROMOTION LAYOUT */}
      <section className="hero-layout-section">
        <div
          className="hero-layout-inner"
          onMouseLeave={handleCategoryMouseLeave}
        >
          {/* Left: Filter & Mega-Menu Sidebar */}
          {activeTab === "products" &&
            storefrontContent.homeLayout.showCategoryMenu && (
            <aside className="home-filter-sidebar home-filter-sidebar-hero">
              <div className="filter-sidebar-header">
                <div className="filter-sidebar-title"></div>
              </div>

              <div className="filter-group category-mega-group">
                {renderCategoryMegaMenu()}
              </div>
            </aside>
          )}

          {/* Dynamic Floating Subcategory Mega-Menu Panel */}
          {activeTab === "products" &&
            storefrontContent.homeLayout.showCategoryMenu &&
            hoveredCategoryObj &&
            hoveredCategoryObj.children.length > 0 && (
            <div
              className="hero-subcategory-panel"
              onMouseEnter={handlePanelMouseEnter}
              onMouseLeave={handleCategoryMouseLeave}
            >
              <div className="hero-subcategory-header">
                <span>{hoveredCategoryObj.name}</span>
                <button
                  type="button"
                  className="see-all-link"
                  onClick={() => handleCategorySelect(hoveredCategoryObj.id)}
                >
                  See all <FiChevronRight size={12} />
                </button>
              </div>
              <div className="hero-subcategory-grid">
                {hoveredCategoryObj.children.map((child) => (
                  <div key={child.id} className="hero-subcategory-column">
                    <button
                      type="button"
                      className={`hero-subcategory-row ${
                        selectedChildCategory === child.id ? "active" : ""
                      }`}
                      onClick={() => {
                        setSelectedCategory(hoveredCategoryObj.id);
                        handleChildCategorySelect(child.id);
                      }}
                    >
                      <span>{child.name}</span>
                    </button>
                    {child.children.length > 0 && (
                      <ul className="hero-subcategory-sub">
                        {child.children.map((grand) => (
                          <li key={grand.id}>
                            <button
                              type="button"
                              className={`hero-subcategory-subrow ${
                                selectedChildCategory === grand.id
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() => {
                                setSelectedCategory(hoveredCategoryObj.id);
                                handleChildCategorySelect(grand.id);
                              }}
                            >
                              {grand.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="hero-center-column">
            <div className="top-search-bar">
              <FiSearch className="top-search-icon" />
              <input
                type="text"
                placeholder={
                  activeTab === "booking"
                    ? "Search booking services..."
                    : "Search products, brands, and categories across store..."
                }
                value={globalSearchQuery}
                onChange={(e) => setGlobalSearchQuery(e.target.value)}
              />
              {globalSearchQuery && (
                <button
                  className="top-search-clear"
                  onClick={() => setGlobalSearchQuery("")}
                  title="Clear search"
                >
                  <FiX />
                </button>
              )}
            </div>

            {/* Center Column: Hero Slider */}
            <div className="hero-main-column">
            <section className={`hero-slider-section storefront-animation-${storefrontContent.animation}`}>
              {heroSlides.length === 0 ? (
                <div className="hero-slider-empty">
                  <div className="hero-placeholder">
                    <h2>Welcome to {tenant.BusinessName || "Our Store"}</h2>
                    <p>
                      {activeTab === "booking"
                        ? "Find a service and book a time that works for you"
                        : "Discover amazing products at great prices"}
                    </p>
                    <Link to="/shop" className="hero-placeholder-btn">
                      {activeTab === "booking" ? "Book a Service →" : "Shop Now →"}
                    </Link>
                  </div>
                </div>
              ) : (
                <Swiper
                  modules={[Autoplay, Pagination, EffectFade]}
                  effect="fade"
                  spaceBetween={0}
                  slidesPerView={1}
                  autoplay={{ delay: 5000, disableOnInteraction: false }}
                  pagination={{ clickable: true, dynamicBullets: true }}
                  loop={heroSlides.length > 1}
                  className="hero-swiper"
                >
                  {heroSlides.map((slide) => (
                    <SwiperSlide key={slide.id}>
                      <div
                        className="hero-slide"
                        style={{
                          backgroundImage: `url(${slide.image || PLACEHOLDER_IMAGE})`,
                          cursor: slide.link ? "pointer" : "default",
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                        onClick={() => handleSlideClick(slide)}
                      >
                        <div className="hero-content">
                          {slide.title && (
                            <span className="hero-badge">{slide.title}</span>
                          )}
                          {slide.subtitle && (
                            <h1 className="hero-title">{slide.subtitle}</h1>
                          )}
                          <button
                            className="hero-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSlideClick(slide);
                            }}
                            style={{
                              backgroundColor: tenant.PrimaryColor || "white",
                              color: tenant.PrimaryColor ? "white" : "#1a1a1a",
                            }}
                          >
                            {slide.buttonText || "Shop Now"} →
                          </button>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </section>
          </div>
          </div>

          {/* Right Column: Promotions + Tenant Branding */}
          {(storefrontContent.homeLayout.showPromotions ||
            storefrontContent.homeLayout.showStoreTile) && (
            <div className="hero-right-column">
              {storefrontContent.homeLayout.showPromotions &&
                (promotionsData && promotionsData.length > 0 ? (
                  <div
                    className="hero-promotion-carousel"
                    role="region"
                    aria-label="Current promotions"
                  >
                    <Swiper
                      modules={[Autoplay, Pagination]}
                      autoplay={
                        promotionCarouselSlides.length > 1
                          ? {
                              delay: 4500,
                              disableOnInteraction: false,
                              pauseOnMouseEnter: true,
                            }
                          : false
                      }
                      pagination={
                        promotionCarouselSlides.length > 1
                          ? { clickable: true }
                          : false
                      }
                      loop={promotionCarouselSlides.length > 1}
                      slidesPerView={1}
                      className="hero-promotion-swiper"
                    >
                      {promotionCarouselSlides.map(({ promotion, product }) => (
                        <SwiperSlide
                          key={
                            `${promotion.PromotionID || promotion.Code || promotion.Name}-${product?.ProductID || "promotion"}`
                          }
                        >
                          <article
                            className={`hero-side-card card-promo${product ? " card-promo-product" : ""}`}
                          >
                            {product && (
                              <img
                                className="hero-promo-product-image"
                                src={product.ImageUrl || getPrimaryImageUrl(product)}
                                alt={product.Name || "Promotion product"}
                                onError={(event) => {
                                  event.currentTarget.onerror = null;
                                  event.currentTarget.src = PLACEHOLDER_IMAGE;
                                }}
                              />
                            )}
                            <div
                              className={`hero-promo-content${product ? "" : " hero-promo-content-text"}`}
                            >
                              <div className="side-card-tag">
                                <FiPercent aria-hidden="true" /> Promotion
                              </div>
                              <div className="hero-promo-copy">
                                <h3>
                                  {product?.Name ||
                                    promotion.Title ||
                                    promotion.Name ||
                                    "Special Deal"}
                                </h3>
                                {product &&
                                  (promotion.Title || promotion.Name) && (
                                    <p className="hero-promo-name">
                                      {promotion.Title || promotion.Name}
                                    </p>
                                  )}
                                <p>
                                  {promotion.Description ||
                                    (promotion.DiscountType === "Percentage"
                                      ? `Save ${promotion.DiscountValue}%`
                                      : `Save R${promotion.DiscountValue}`)}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => navigate("/shop")}
                              >
                                Shop Now <FiArrowRight aria-hidden="true" />
                              </button>
                            </div>
                          </article>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                ) : (
                  <div className="hero-side-card card-flash">
                    <div className="side-card-tag">
                      <FiZap /> Hot Deal
                    </div>
                    <h3>Trending Collections</h3>
                    <p>Special discounts updated daily</p>
                    <button onClick={() => navigate("/shop")}>
                      Explore <FiArrowRight />
                    </button>
                  </div>
                ))}

              {storefrontContent.homeLayout.showStoreTile && (
                <div className="hero-side-card card-tenant-logo">
                  <div className="tenant-logo-inner">
                    {tenantLogo ? (
                      <img
                        src={tenantLogo}
                        alt={tenant?.BusinessName || "Store logo"}
                        className="tenant-logo-img"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = PLACEHOLDER_IMAGE;
                        }}
                      />
                    ) : tenantHeroImage ? (
                      <img
                        src={tenantHeroImage}
                        alt={tenant?.BusinessName || "Store"}
                        className="tenant-logo-img"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = PLACEHOLDER_IMAGE;
                        }}
                      />
                    ) : (
                      <div className="tenant-logo-fallback">
                        <FiShield size={28} />
                        <span>{tenant?.BusinessName || "Our Store"}</span>
                      </div>
                    )}
                  </div>
                  {tenant?.BusinessName && (
                    <h3 className="tenant-logo-name">{tenant.BusinessName}</h3>
                  )}
                  <p className="tenant-logo-tagline">
                    {settings?.Tagline ||
                      tenant?.Tagline ||
                      "Shop with confidence"}
                  </p>
                  <button onClick={() => navigate("/shop")}>
                    Visit Shop <FiArrowRight />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* DESKTOP / TABLET CATEGORY CARDS */}
      {activeTab === "products" && categories.length > 0 && (
        <section className="categories-section">
          <div className="container">
            <div className="categories-grid">
              {categoryCardsToShow.map((category) => {
                const isImage = category.iconType === "image" && category.icon;
                const isSelected = selectedCategory === category.id;

                return (
                  <div
                    key={category.id}
                    className={`category-card ${isSelected ? "active" : ""}`}
                    onClick={() => {
                      handleCategorySelect(category.id);
                      setHoveredCategory(category.id);
                    }}
                    title={
                      isSelected
                        ? "Click again to clear this category"
                        : category.name
                    }
                  >
                    <div className="category-icon-wrapper">
                      {isImage ? (
                        <img
                          src={category.icon}
                          alt={category.name}
                          className="category-image-icon"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = PLACEHOLDER_IMAGE;
                          }}
                        />
                      ) : (
                        <span className="category-icon">{category.icon}</span>
                      )}
                    </div>
                    <span className="category-name">{category.name}</span>

                    {isSelected ? (
                      <span
                        className="category-selected-badge"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearCategoryLevel();
                        }}
                        title="Clear category"
                      >
                        <FiX size={10} />
                      </span>
                    ) : (
                      category.hasChildren && (
                        <span className="category-chevron">
                          <FiChevronDown size={16} />
                        </span>
                      )
                    )}
                  </div>
                );
              })}
            </div>
            {categories.length > MAX_VISIBLE_ROOT_CATEGORIES && (
              <div className="categories-show-more">
                <button
                  type="button"
                  className="category-show-more-btn"
                  onClick={() => setShowAllCategories((current) => !current)}
                >
                  {showAllCategories ? "Show less" : "Show more categories"}
                  <FiChevronDown
                    size={16}
                    style={{
                      transform: showAllCategories ? "rotate(180deg)" : "none",
                    }}
                  />
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Toggle Tabs */}
      {showTabs && (
        <div className="toggle-tabs-section">
          <div className="container">
            <div
              className={`toggle-tabs-wrapper toggle-tabs-wrapper--${bookingToggle.homeStyle} toggle-tabs-wrapper--size-${bookingToggle.homeSize}`}
              style={{ "--toggle-accent": storefrontPrimaryColor }}
              role="tablist"
              aria-label="Browse products or bookings"
            >
              <button
                className={`toggle-tab ${activeTab === "products" ? "active" : ""}`}
                onClick={() => setActiveTab("products")}
                role="tab"
                aria-selected={activeTab === "products"}
              >
                <FiPackage className="tab-icon" />
                <span>{bookingToggle.productLabel}</span>
                {bookingToggle.showCounts && allProducts.length > 0 && (
                  <span className="tab-badge">{allProducts.length}</span>
                )}
              </button>
              <div
                className={`toggle-slider ${activeTab === "booking" ? "right" : "left"}`}
              />
              <button
                className={`toggle-tab ${activeTab === "booking" ? "active" : ""}`}
                onClick={() => setActiveTab("booking")}
                role="tab"
                aria-selected={activeTab === "booking"}
              >
                <FiCalendar className="tab-icon" />
                <span>{bookingToggle.bookingLabel}</span>
                {bookingToggle.showCounts && bookings.length > 0 && (
                  <span className="tab-badge">{bookings.length}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "products" &&
        renderStorefrontBlocks("betweenHeroAndProducts")}

      {/* Products Tab Content */}
      {showProducts && (activeTab === "products" || !showTabs) && (
        <section className={`featured-section storefront-animation-${storefrontContent.animation}`}>
          <div className="home-products-content-wrapper home-products-content-wrapper-full">
            <div className="home-products-content">
              <div className="home-products-header">
                <div className="home-products-count">
                  {visibleProducts.length} of {productsWithReviews.length}{" "}
                  products
                </div>
                <div className="home-products-actions">
                  {storefrontContent.productGrid.showRandomize && <button
                    type="button"
                    className="home-products-action-btn random"
                    onClick={handleRandomize}
                    disabled={isRandomizing || productsLoading}
                  >
                    <FiRefreshCw
                      className={isRandomizing ? "spinning" : ""}
                      size={16}
                    />
                    <span>Random</span>
                  </button>}
                  {storefrontContent.productGrid.showFilters && <button
                    type="button"
                    className={`home-products-action-btn filters mobile-filter-toggle ${hasActiveFilters ? "active" : ""}`}
                    onClick={toggleMobileFilters}
                    aria-expanded={isFilterOpen}
                    aria-controls="home-product-filters"
                  >
                    <FiSliders size={16} />
                    <span>Filters</span>
                    {hasActiveFilters && <span className="filter-dot" />}
                  </button>}
                </div>
              </div>

              {/* Mobile Filter Sidebar Drawer */}
              {createPortal(
                <div
                  id="home-product-filters"
                  className={`home-filter-sidebar-mobile ${isFilterOpen ? "open" : "closed"}`}
                >
                  <div className="filter-sidebar-header">
                    <div className="filter-sidebar-title">
                      <FiFilter />
                      <span>Filters</span>
                      {hasActiveFilters && <span className="filter-dot" />}
                    </div>
                    <div className="filter-sidebar-actions">
                      {hasActiveFilters && (
                        <button
                          className="reset-filters-btn"
                          onClick={resetFilters}
                        >
                          Reset All
                        </button>
                      )}
                      <button
                        className="filter-close-btn"
                        onClick={toggleMobileFilters}
                        aria-label="Close filters"
                      >
                        <FiX />
                      </button>
                    </div>
                  </div>

                  <div className="filter-group category-mega-group">
                    {renderCategoryMegaMenu()}
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Price Range</label>
                    <div className="filter-price-range">
                      <div className="filter-price-inputs">
                        <div className="filter-price-input-group">
                          <span className="filter-price-currency">R</span>
                          <input
                            type="number"
                            value={priceRange.min}
                            onChange={(e) =>
                              setPriceRange({
                                ...priceRange,
                                min: Math.max(0, parseInt(e.target.value) || 0),
                              })
                            }
                            min="0"
                            className="filter-price-input"
                            placeholder="Min"
                          />
                        </div>
                        <span className="filter-price-separator">to</span>
                        <div className="filter-price-input-group">
                          <span className="filter-price-currency">R</span>
                          <input
                            type="number"
                            value={priceRange.max}
                            onChange={(e) =>
                              setPriceRange({
                                ...priceRange,
                                max: parseInt(e.target.value) || 10000,
                              })
                            }
                            min="0"
                            className="filter-price-input"
                            placeholder="Max"
                          />
                        </div>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="10000"
                        value={priceRange.max}
                        onChange={(e) =>
                          setPriceRange({
                            ...priceRange,
                            max: parseInt(e.target.value),
                          })
                        }
                        className="filter-price-slider"
                      />
                    </div>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Sort By</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="filter-sort-select"
                    >
                      <option value="featured">Featured</option>
                      <option value="newest">Newest</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="popular">Most Popular</option>
                    </select>
                  </div>
                </div>,
                document.body,
              )}

              {/* Product Grid */}
              {visibleProducts.length > 0 ? (
                <>
                  <ProductGrid
                    products={visibleProducts}
                    loading={false}
                    onAddToCart={handleAddToCart}
                    addingProductId={addingProductId}
                    promotions={promotionsData}
                    getProductPromotion={getProductPromotion}
                    gridConfig={storefrontContent.productGrid}
                    interstitials={inlineHeroBanners.map((slide) => ({
                      id: slide.id,
                      afterProducts: slide.afterProducts,
                      content: <StorefrontInlineBanner slide={slide} />,
                    }))}
                  />

                  {hasMoreProducts && (
                    <div className="show-more-container">
                      <button
                        className="show-more-btn"
                        onClick={handleShowMore}
                      >
                        Show More Products
                        <span className="show-more-count">
                          +
                          {Math.min(
                            PRODUCTS_INCREMENT,
                            productsWithReviews.length - visibleProductsCount,
                          )}
                        </span>
                        <FiChevronDown />
                      </button>
                      <div className="show-more-progress">
                        <div
                          className="show-more-progress-bar"
                          style={{
                            width: `${
                              (visibleProductsCount /
                                productsWithReviews.length) *
                              100
                            }%`,
                          }}
                        />
                      </div>
                      <div className="show-more-info">
                        Showing {visibleProductsCount} of{" "}
                        {productsWithReviews.length} products
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">🔍</div>
                  <h3>No products found</h3>
                  <p>Try adjusting your search query or filters</p>
                  {hasActiveFilters && (
                    <button
                      className="reset-filters-btn"
                      onClick={resetFilters}
                    >
                      Reset Filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Booking Tab Content */}
      {showBookings && (activeTab === "booking" || !showTabs) && (
        <section
          className={`booking-section booking-section--${bookingToggle.bookingCardStyle}`}
        >
          <div className="container">
            <div className="section-header">
              <div>
                <span className="section-subtitle">Book an Appointment</span>
                <h2>Services &amp; Appointments</h2>
                <p className="section-description">
                  Schedule your appointment at your convenience. Our experts are
                  ready to help you.
                </p>
              </div>
            </div>
            <BookingGrid
              bookings={visibleBookings}
              loading={bookingLoading}
              onBookNow={handleBookNow}
              primaryColor={tenant?.PrimaryColor}
              cardStyle={bookingToggle.bookingCardStyle}
            />
          </div>
        </section>
      )}

      {/* Trust Badges Section */}
      <section className="trust-badges-section">
        <div className="container">
          <div className="trust-badges-grid">
            {trustBadges.map((badge, index) => (
              <div key={index} className="trust-badge">
                <div className="trust-badge-icon">{badge.icon}</div>
                <div className="trust-badge-content">
                  <h3>{badge.title}</h3>
                  <p>{badge.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {renderStorefrontBlocks("betweenProductsAndReviews")}

      {/* Testimonials Section */}
      {displayReviews.length > 0 && (
        <section className="testimonials-section">
          <div className="container">
            <div className="testimonials-header">
              <span className="testimonials-subtitle">
                What Our Customers Say
              </span>
              <h2 className="testimonials-title">
                Real Reviews, Real Experiences
              </h2>
              <p className="testimonials-description">
                Join thousands of satisfied customers who love shopping with us
              </p>
              <div className="testimonials-rating-badge">
                <span className="rating-badge-score">{averageRating}</span>
                <div className="rating-badge-stars">
                  {renderStars(Math.round(Number(averageRating)))}
                </div>
                <span className="rating-badge-text">
                  Based on {recentReviewSummary?.totalReviews || 0} customer{" "}
                  {recentReviewSummary?.totalReviews === 1
                    ? "review"
                    : "reviews"}
                </span>
              </div>
            </div>

            <div className="testimonials-grid">
              {displayReviews.map((review, index) => (
                <div
                  key={review.ReviewID || index}
                  className="testimonial-card"
                >
                  {renderStars(review.Rating || 0)}
                  <p className="testimonial-message">
                    "
                    {(review.Comment || "").length > 120
                      ? `${(review.Comment || "").substring(0, 120)}...`
                      : review.Comment || "Great product!"}
                    "
                  </p>
                  <div className="testimonial-customer">
                    <div
                      className="customer-avatar"
                      style={{
                        background: `linear-gradient(135deg, ${getAvatarColor(review.ReviewerName)}, ${getAvatarColor(review.ReviewerName)}dd)`,
                      }}
                    >
                      {getInitials(review.ReviewerName)}
                    </div>
                    <div className="customer-info">
                      <h4>{review.ReviewerName || "Verified Customer"}</h4>
                      <p>on {review.productName || "Product"}</p>
                      <div className="customer-review-meta">
                        <FiClock size={10} />
                        <span>
                          {review.CreatedAt
                            ? new Date(review.CreatedAt).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                },
                              )
                            : "Recently"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="testimonials-footer">
              <Link to="/reviews" className="view-all-reviews-btn">
                Read All Reviews <FiChevronRight />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* MOBILE CATEGORY SHEET */}
      {activeTab === "products" &&
        storefrontContent.homeLayout.showCategoryMenu &&
        showMobileCategorySheet && (
        <>
          <div
            className="mobile-category-overlay"
            onClick={closeMobileCategorySheet}
          />
          <div className="mobile-category-sheet">
            <div className="mobile-category-sheet-handle" />
            <div className="mobile-category-sheet-header">
              <span className="mobile-category-sheet-title">
                {mobileCategoryDrilldown
                  ? categoryTree.find((c) => c.id === mobileCategoryDrilldown)
                      ?.name
                  : "Browse Categories"}
              </span>
              <button
                className="mobile-category-sheet-close"
                onClick={closeMobileCategorySheet}
                aria-label="Close"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="mobile-category-sheet-body">
              {mobileCategoryDrilldown ? (
                <>
                  <button
                    className="mobile-subcategory-back"
                    onClick={() => setMobileCategoryDrilldown(null)}
                  >
                    <FiChevronLeft size={16} /> Back to categories
                  </button>

                  <div className="mobile-subcategory-list">
                    {categoryTree
                      .find((c) => c.id === mobileCategoryDrilldown)
                      ?.children.map((child) => (
                        <button
                          key={child.id}
                          className={`mobile-subcategory-item ${
                            selectedChildCategory === child.id ? "active" : ""
                          }`}
                          onClick={() => handleMobileChildSelect(child.id)}
                        >
                          <span>{child.name}</span>
                          {child.children.length > 0 && (
                            <FiChevronRight size={16} />
                          )}
                        </button>
                      ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="mobile-category-grid">
                    <button
                      className={`mobile-category-chip ${
                        selectedCategory === "all" && !selectedChildCategory
                          ? "active"
                          : ""
                      }`}
                      onClick={() => handleMobileCategorySelect("all")}
                    >
                      <span className="mobile-category-chip-icon">🏠</span>
                      <span className="mobile-category-chip-name">All</span>
                    </button>

                    {categories.map((cat) => {
                      const isImage = cat.iconType === "image" && cat.icon;
                      const isSelected =
                        selectedCategory === cat.id && !selectedChildCategory;

                      return (
                        <button
                          key={cat.id}
                          className={`mobile-category-chip ${
                            isSelected ? "active" : ""
                          }`}
                          onClick={() => handleMobileCategorySelect(cat.id)}
                        >
                          {isImage ? (
                            <img
                              src={cat.icon}
                              alt={cat.name}
                              className="mobile-category-chip-img"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = PLACEHOLDER_IMAGE;
                              }}
                            />
                          ) : (
                            <span className="mobile-category-chip-icon">
                              {cat.icon}
                            </span>
                          )}
                          <span className="mobile-category-chip-name">
                            {cat.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {(selectedCategory !== "all" || selectedChildCategory) && (
                    <button
                      className="mobile-category-clear"
                      onClick={() => {
                        clearCategoryLevel();
                        closeMobileCategorySheet();
                      }}
                    >
                      <FiX size={14} /> Clear category filter
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Booking Modal */}
      {showBookingModal && (
        <BookingModal
          isOpen={showBookingModal}
          onClose={handleBookingModalClose}
          product={selectedBooking}
          onSuccess={handleBookingSuccess}
        />
      )}
    </div>
  );
};

export default Home;
